import { useCallback, useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import {
  REPOSITORY_RELOCATE_MESSAGE,
  fetchClientRepositoryConfig,
  fetchRepositoryMode,
  pickClientRepositoryRootPath,
  saveClientRepositoryConfig,
} from "@/lib/clientRepositoryConfig";
import { getStoredClientId } from "@/lib/clientSelection";

/**
 * Diálogo opcional si la ruta de exportación existía y dejó de estar en disco.
 * No se muestra al cambiar de cliente si nunca se configuró carpeta (CFDI en MongoDB).
 */
export default function ClientRepositoryRelocateDialog({ enabled = true }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [clientId, setClientId] = useState("");
  const [config, setConfig] = useState(null);
  const [serverManaged, setServerManaged] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    fetchRepositoryMode()
      .then((data) => setServerManaged(Boolean(data?.server_managed)))
      .catch(() => setServerManaged(false));
  }, [enabled]);

  const validateClient = useCallback(async (id) => {
    if (!enabled || !id) return;
    try {
      const cfg = await fetchClientRepositoryConfig(id);
      if (cfg?.server_managed || serverManaged) {
        return;
      }
      if (cfg?.needs_relocation && cfg?.configured) {
        setClientId(id);
        setConfig(cfg);
        setOpen(true);
      }
    } catch {
      /* Validación silenciosa: no bloquear la app si falla la red */
    }
  }, [enabled, serverManaged]);

  useEffect(() => {
    if (!enabled) return;
    validateClient(getStoredClientId());
  }, [enabled, validateClient]);

  useEffect(() => {
    if (!enabled) return;
    const onClientChanged = (e) => {
      const id = e?.detail?.clientId || getStoredClientId();
      validateClient(id);
    };
    window.addEventListener("visor:client-changed", onClientChanged);
    return () => window.removeEventListener("visor:client-changed", onClientChanged);
  }, [enabled, validateClient]);

  const onRelocate = async () => {
    if (!clientId) return;
    setBusy(true);
    try {
      const rootPath = await pickClientRepositoryRootPath();
      const saved = await saveClientRepositoryConfig(clientId, rootPath);
      toast.success(saved?.message || "Repositorio local restablecido.");
      setOpen(false);
      setConfig(null);
      window.dispatchEvent(
        new CustomEvent("visor:client-repository-ready", { detail: { clientId, config: saved } }),
      );
    } catch (e) {
      if (e?.message) toast.error(e.message);
      else toast.error(formatApiError(e.response?.data?.detail) || "No se pudo restablecer la ruta.");
    } finally {
      setBusy(false);
    }
  };

  if (!enabled) return null;

  return (
    <AlertDialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
      <AlertDialogContent data-testid="client-repository-relocate-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Carpeta local no encontrada</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-slate-600">
              <p>{REPOSITORY_RELOCATE_MESSAGE}</p>
              {config?.root_path ? (
                <p className="text-xs font-mono bg-slate-50 border border-slate-200 rounded-sm px-2 py-1 break-all">
                  Ruta registrada: {config.root_path}
                </p>
              ) : null}
              {config?.client_rfc ? (
                <p className="text-xs">Cliente: {config.client_rfc}</p>
              ) : null}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel disabled={busy} className="rounded-sm">
            Más tarde
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={(e) => {
              e.preventDefault();
              onRelocate();
            }}
            className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]"
            data-testid="client-repository-relocate-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Seleccionar nueva ubicación
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
